import { useGetFrontRichiesteIdProcuratore } from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { type PersonalizzazioniRiepilogoDTO } from '@framework/api-client/api/inquiries-compile/schemas/personalizzazioniRiepilogoDTO.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { SectionTitle } from '@framework/ui/components/summary/SectionTitle.tsx';
import { Col, Row } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TbUsers } from 'react-icons/tb';
import { ModuleFormSource } from '../../../module/form/ModuleFormSource.ts';
import { useSummarySource } from '../SummarySourceContext.tsx';
import { getPersonalDataItems } from './detail/getPersonalDataItems.ts';
import { SummaryPersonalDataItem } from './detail/SummaryPersonalDataItem.tsx';

export interface ISummaryPersonalDataSectionProps {
  submission?: RichiestaDTO;
  submissionId: string;
  customizations?: PersonalizzazioniRiepilogoDTO | null;
}

/**
 * Sezione del riepilogo che mostra i dati anagrafici del titolare,
 * del soggetto giuridico, del procuratore e degli altri soggetti
 * coinvolti nella richiesta.
 */
export function SummaryPersonalDataSection(
  props: ISummaryPersonalDataSectionProps,
) {
  const { submission, submissionId, customizations } = props;
  const { t } = useTranslation();
  const isBackoffice = useSummarySource() === ModuleFormSource.BACKOFFICE;

  const applicant = useMemo(
    () => submission?.datiTitolari?.find(d => d.flTitolarePrincipale),
    [submission?.datiTitolari],
  );

  // Gli altri elementi di `datiTitolari` (gestori, delegati moduli, ...),
  // previsti dalla spec per il solo riepilogo di Backoffice.
  const otherSubjects = useMemo(
    () =>
      isBackoffice
        ? (submission?.datiTitolari?.filter(d => !d.flTitolarePrincipale) ?? [])
        : [],
    [isBackoffice, submission?.datiTitolari],
  );

  const { data: attorneyData } = useGetFrontRichiesteIdProcuratore(
    submissionId,
    {},
    {
      query: {
        enabled:
          Boolean(submissionId) && !customizations?.procuratore?.nascondi,
      },
    },
  );
  const items = useMemo(
    () =>
      getPersonalDataItems({
        applicant,
        attorney: attorneyData?.datiProcura,
        otherSubjects,
        customizations,
      }),
    [applicant, attorneyData, otherSubjects, customizations],
  );

  if (!items.length) return null;

  return (
    <div>
      <SectionTitle
        icon={<TbUsers />}
        title={t('submission-summary.dati-anagrafici', 'Dati Anagrafici')}
      />
      <VerticalSpace />
      <Row gutter={[24, 24]}>
        {items.map(item => (
          <Col key={item.id} xs={24} md={24} lg={12}>
            <SummaryPersonalDataItem item={item} />
          </Col>
        ))}
      </Row>
    </div>
  );
}
